import { Injectable } from '@angular/core';
import * as momentTz from 'moment-timezone';

import { DropdownItem, TimezonesByOffset } from '../models/misc.model';

/**
 * This service is used by AJS and NG.
 * Some methods should not have timezone in param (getMomentWithTimeZone(), formatOffsetForTimezone()),
 * but AJS don't like injection of AlcmeonContextService (circular dependency).
 *
 * TODO : when there is no more AJS, inject alcmeonContextService and remove userTimezone from params.
 */
@Injectable({
  providedIn: 'root',
})
export class TimezoneService {
  /**
   * Formats the given timezones, retrieved from /self, to the following format:
   * [{
   *  id: 'Europe/Paris',
   *  label: '(UTC+02:00) Europe/Paris'
   * },...]
   * The result is sorted by UTC offset and alphabetically for a given offset
   *
   * The given timezones are expected to be grouped by offset in minutes:
   * {"-300": ['America/Mexico_City', 'America/Monterrey', ...], ...}
   * They are expected to be sorted alphabetically within each offset
   */
  public buildTimezoneDropdownItems(allTimezones: TimezonesByOffset): DropdownItem[] {
    let result = [];
    Object.keys(allTimezones)
      .map(minutes => Number(minutes))
      .sort((a, b) => a - b) // sort() sorts alphabetically by default, even for numbers
      .forEach(offset => (result = result.concat(this.formatTimezones(allTimezones, offset))));
    return result;
  }

  // offset is a number, in minutes
  private formatTimezones(allTimezones: TimezonesByOffset, offset: number): DropdownItem[] {
    const timezones = allTimezones[offset.toString()];
    return timezones.map(timezoneCode => ({
      id: timezoneCode,
      label: `${this.formatUtcOffset(offset)} ${timezoneCode}`,
    }));
  }

  /**
   * Backend sends all list of offset by timezone, with DST, for the present moment, not for a random given date.
   * So the offset can be wrong for a given date, due to wrong DST.
   *
   * We use moment-timezone (which relies on "Iana time zone database" of all timezones/countries/DST) to get the right offset for a given date.
   *
   * @param date desired date
   * @param timezone desired timezone
   * @returns offset in minute, positive or negative
   */
  private getOffsetForTimezone(date: string, timezone: string) {
    try {
      return momentTz.utc(date).tz(timezone).utcOffset();
    } catch {
      // If the timezone is not correct, keep the date as utc.
      return momentTz.utc(date).utcOffset();
    }
  }

  /**
   * Format offset for diplay or date pipe
   * @param offset number offset in minutes (positive or negative)
   * @output string offset as <sign>hour:minutes
   */
  private formatOffset(offset: number): string {
    const sign = offset < 0 ? '-' : '+';
    const hours = this.zeroPad2Digits(Math.floor(Math.abs(offset) / 60));
    const minutes = this.zeroPad2Digits(Math.abs(offset) % 60);
    return `${sign}${hours}:${minutes}`;
  }

  /**
   * Format offset for diplay or date pipe with given timezone
   * @param userTimezone we should get it in alcmeonContextService.self.timezone, but not possible with AJS.
   * TODO remove this param when there is no more AJS, and inject alcmeonContextService.
   * @output string offset as <sign>hour:minutes
   */
  public formatOffsetForTimezone(date: string, userTimezone: string): string {
    const offset = this.getOffsetForTimezone(date, userTimezone);
    return this.formatOffset(offset);
  }

  private formatUtcOffset(offset: number): string {
    const formatedOffset = this.formatOffset(offset);
    return `(UTC${formatedOffset})`;
  }

  private zeroPad2Digits(n: number) {
    return `0${n}`.slice(-2);
  }

  /**
   * @param userTimezone we should get it in alcmeonContextService.self.timezone, but not possible with AJS.
   * TODO remove this param when there is no more AJS, and inject alcmeonContextService.
   * @param date string representing a date time in UTC (eg : "2023-12-31 23:59"), or iso string. Can be undefined to get the current date time.
   * @returns moment object
   */
  public getMomentWithTimeZone(userTimezone: string, date?: string): moment.Moment {
    return momentTz.utc(date).tz(userTimezone);
  }
}
